import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
	Box,
	Button,
	Group,
	Input,
	Loader,
	Paper,
	Select,
	Text,
	TextInput,
	Title,
	useMantineTheme,
} from '@mantine/core'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import ArrowLeft from 'tabler-icons-react/dist/icons/arrow-left'
import {
	getCurrentUserPositionsForOrg,
	switchExperience,
	switchOrg,
	updateCurrentUserProfile,
} from '../services/actions/userActions'
import { loadUserContext } from '../services/utils/loadUserContext'
import { toast } from '../services/utils/toast'

const buildExperienceOptions = (positions) => {
	const byExperienceId = new Map()
	;(positions || []).forEach((position) => {
		const experienceId = position?.experienceId
		if (!experienceId || byExperienceId.has(experienceId)) return
		byExperienceId.set(experienceId, {
			value: experienceId,
			label:
				position?.experienceName || position?.experienceSlug || experienceId,
			experienceSlug: position?.experienceSlug || '',
		})
	})
	return Array.from(byExperienceId.values()).sort((a, b) =>
		String(a.label || '').localeCompare(String(b.label || '')),
	)
}

const AccountSettingsPage = () => {
	const theme = useMantineTheme()
	const dispatch = useDispatch()
	const authUser = useSelector((state) => state.user.authUser)
	const orgMemberships = useSelector((state) => state.user.orgMemberships || [])
	const activeOrgMemberships = useMemo(
		() => orgMemberships.filter((membership) => membership.isActive !== false),
		[orgMemberships],
	)
	const currentOrg = useSelector((state) => state.user.currentOrg)
	const currentExperience = useSelector((state) => state.user.currentExperience)

	const [name, setName] = useState('')
	const [phone, setPhone] = useState('')
	const [selectedOrgSlug, setSelectedOrgSlug] = useState('')
	const [selectedExperienceByOrg, setSelectedExperienceByOrg] = useState({})
	const [experienceOptionsByOrg, setExperienceOptionsByOrg] = useState({})
	const [isLoadingExperiences, setIsLoadingExperiences] = useState(false)
	const [experienceLoadError, setExperienceLoadError] = useState('')
	const [error, setError] = useState('')
	const [isSaving, setIsSaving] = useState(false)
	const [baseline, setBaseline] = useState(null)

	useEffect(() => {
		if (!authUser) return
		if (baseline) return

		const fallbackOrgSlug =
			currentOrg?.orgSlug || activeOrgMemberships[0]?.orgSlug || ''
		const currentExperienceId = currentExperience?.id || ''

		setName(authUser?.name || '')
		setPhone(authUser?.phone || '')
		setSelectedOrgSlug(fallbackOrgSlug)
		setSelectedExperienceByOrg((previous) => ({
			...previous,
			[fallbackOrgSlug]: currentExperienceId || '',
		}))
		setBaseline({
			name: authUser?.name || '',
			phone: authUser?.phone || '',
			orgSlug: fallbackOrgSlug,
			experienceId: currentExperienceId || '',
		})
	}, [
		authUser,
		baseline,
		currentExperience,
		currentOrg?.orgSlug,
		activeOrgMemberships,
	])

	useEffect(() => {
		if (!selectedOrgSlug) return
		if (experienceOptionsByOrg[selectedOrgSlug]) return

		let cancelled = false

		const loadOptions = async () => {
			setIsLoadingExperiences(true)
			setExperienceLoadError('')
			try {
				const positions = await getCurrentUserPositionsForOrg(selectedOrgSlug)
				if (cancelled) return
				const options = buildExperienceOptions(positions)
				setExperienceOptionsByOrg((previous) => ({
					...previous,
					[selectedOrgSlug]: options,
				}))
				setSelectedExperienceByOrg((previous) => {
					const currentValue = previous[selectedOrgSlug] || ''
					if (
						currentValue &&
						options.some((option) => option.value === currentValue)
					) {
						return previous
					}

					const contextExperienceId =
						selectedOrgSlug === currentOrg?.orgSlug
							? currentExperience?.id || ''
							: ''

					const nextValue = options.some(
						(option) => option.value === contextExperienceId,
					)
						? contextExperienceId
						: options.length === 1
							? options[0].value
							: ''

					return {
						...previous,
						[selectedOrgSlug]: nextValue,
					}
				})
			} catch (err) {
				if (!cancelled) {
					setExperienceLoadError(
						err.message || 'Failed to load experience options.',
					)
				}
			} finally {
				if (!cancelled) {
					setIsLoadingExperiences(false)
				}
			}
		}

		loadOptions()

		return () => {
			cancelled = true
		}
	}, [
		currentExperience,
		currentOrg?.orgSlug,
		experienceOptionsByOrg,
		selectedOrgSlug,
	])

	const selectedExperienceOptions = useMemo(
		() => experienceOptionsByOrg[selectedOrgSlug] || [],
		[experienceOptionsByOrg, selectedOrgSlug],
	)
	const selectedExperienceId = selectedExperienceByOrg[selectedOrgSlug] || ''
	const isPhoneValid = !phone || isValidPhoneNumber(phone)
	const hasNoActiveExperiences =
		!!selectedOrgSlug &&
		!isLoadingExperiences &&
		!experienceLoadError &&
		selectedExperienceOptions.length === 0

	const hasUnsavedChanges = useMemo(() => {
		if (!baseline) return false

		const normalizedName = String(name || '').trim()
		const normalizedBaselineName = String(baseline?.name || '').trim()
		const normalizedPhone = String(phone || '').trim()
		const normalizedBaselinePhone = String(baseline?.phone || '').trim()
		const orgChanged = selectedOrgSlug !== baseline.orgSlug
		let effectiveBaselineExperienceId = String(baseline?.experienceId || '')
		if (
			!orgChanged &&
			effectiveBaselineExperienceId &&
			!selectedExperienceOptions.some(
				(option) => option.value === effectiveBaselineExperienceId,
			)
		) {
			effectiveBaselineExperienceId = ''
		}
		const experienceChanged =
			!orgChanged &&
			String(selectedExperienceId || '') !== effectiveBaselineExperienceId

		return (
			normalizedName !== normalizedBaselineName ||
			normalizedPhone !== normalizedBaselinePhone ||
			orgChanged ||
			experienceChanged
		)
	}, [
		baseline,
		name,
		phone,
		selectedExperienceId,
		selectedExperienceOptions,
		selectedOrgSlug,
	])

	useEffect(() => {
		if (!hasUnsavedChanges || isSaving) return

		const warningMessage =
			'You have unsaved changes. Leave this page without saving?'
		let isRevertingPop = false
		const historyRef = window.history
		const originalPushState = historyRef.pushState.bind(historyRef)
		const originalReplaceState = historyRef.replaceState.bind(historyRef)

		const shouldNavigate = () => window.confirm(warningMessage)

		const handleBeforeUnload = (event) => {
			event.preventDefault()
			event.returnValue = ''
		}

		const guardedPushState = (...args) => {
			if (shouldNavigate()) {
				return originalPushState(...args)
			}
			return undefined
		}

		const guardedReplaceState = (...args) => {
			if (shouldNavigate()) {
				return originalReplaceState(...args)
			}
			return undefined
		}

		const handlePopState = () => {
			if (isRevertingPop) {
				isRevertingPop = false
				return
			}

			if (shouldNavigate()) return

			isRevertingPop = true
			window.history.go(1)
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		window.addEventListener('popstate', handlePopState)
		historyRef.pushState = guardedPushState
		historyRef.replaceState = guardedReplaceState

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload)
			window.removeEventListener('popstate', handlePopState)
			historyRef.pushState = originalPushState
			historyRef.replaceState = originalReplaceState
		}
	}, [hasUnsavedChanges, isSaving])

	const handleSave = async () => {
		const normalizedName = String(name || '').trim()
		if (!normalizedName) {
			setError('Name is required')
			return
		}
		if (!isPhoneValid) {
			setError('Enter a valid phone number or clear the field.')
			return
		}
		if (!selectedOrgSlug) {
			setError('Current organization is required')
			return
		}
		if (selectedExperienceOptions.length > 1 && !selectedExperienceId) {
			setError('Select a current experience for the selected organization.')
			return
		}

		setError('')
		setIsSaving(true)

		try {
			await updateCurrentUserProfile({
				name: normalizedName,
				phone,
			})

			const currentOrgSlug = currentOrg?.orgSlug || ''
			const isOrgChanged = selectedOrgSlug !== currentOrgSlug

			let switchOrgResponse = null
			if (isOrgChanged) {
				switchOrgResponse = await switchOrg(selectedOrgSlug)
			}

			if (selectedExperienceId) {
				const selectedExperience = selectedExperienceOptions.find(
					(option) => option.value === selectedExperienceId,
				)
				if (selectedExperience?.experienceSlug) {
					const currentExperienceId = isOrgChanged
						? switchOrgResponse?.currentExperienceId || ''
						: currentExperience?.id || ''

					if (selectedExperienceId !== currentExperienceId) {
						await switchExperience(selectedExperience.experienceSlug)
					}
				}
			}

			await loadUserContext(dispatch)

			setBaseline({
				name: normalizedName,
				phone: String(phone || '').trim(),
				orgSlug: selectedOrgSlug,
				experienceId: selectedExperienceId || '',
			})
			toast.success({
				title: 'Saved',
				message: 'Account settings updated.',
			})
		} catch (err) {
			const message = err.message || 'Failed to save account settings.'
			setError(message)
			toast.error({
				title: 'Save failed',
				message,
			})
		} finally {
			setIsSaving(false)
		}
	}

	const orgOptions = activeOrgMemberships.map((membership) => ({
		value: membership.orgSlug,
		label: membership.orgName,
	}))
	const showOrgSelector = orgOptions.length > 1
	const showExperienceSelector = selectedExperienceOptions.length > 1

	if (!authUser || !baseline) {
		return (
			<Box px='md' py='md'>
				<Loader />
			</Box>
		)
	}

	return (
		<Box px='md' py='md'>
			<Group position='apart' mb='md' align='center'>
				<Title order={2}>Account Settings</Title>
				<Button
					component={Link}
					to='/'
					variant='subtle'
					color='gray'
					leftIcon={<ArrowLeft size={16} />}
				>
					Back to Home
				</Button>
			</Group>
			<Paper withBorder radius='md' p='lg' maw={760}>
				{error && (
					<Text color='red' mb='md'>
						{error}
					</Text>
				)}

				<TextInput
					label='Name'
					value={name}
					onChange={(event) => setName(event.currentTarget.value)}
					mb='sm'
				/>
				<TextInput
					label='Email'
					value={authUser?.email || ''}
					readOnly
					mb='sm'
				/>
				<Input.Wrapper
					label='Phone'
					error={phone && !isPhoneValid ? 'Enter a valid phone number' : null}
					mb='md'
				>
					<PhoneInput
						defaultCountry='US'
						international
						countryCallingCodeEditable={false}
						value={phone || undefined}
						onChange={(value) => setPhone(value || '')}
						className='phone-input'
						style={{
							'--phone-input-background': theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
							'--phone-input-border': theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4],
							'--phone-input-color': theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
							'--phone-input-color-scheme': theme.colorScheme,
						}}
					/>
				</Input.Wrapper>

				{showOrgSelector && (
					<Select
						label='Current Organization'
						value={selectedOrgSlug}
						onChange={(value) => setSelectedOrgSlug(value || '')}
						data={orgOptions}
						mb='md'
					/>
				)}

				{isLoadingExperiences && (
					<Group spacing='xs' mb='sm'>
						<Loader size='xs' />
						<Text size='sm' color='dimmed'>
							Loading experiences...
						</Text>
					</Group>
				)}
				{experienceLoadError && (
					<Text color='red' size='sm' mb='sm'>
						{experienceLoadError}
					</Text>
				)}
				{hasNoActiveExperiences && (
					<Text color='dimmed' size='sm' mb='sm'>
						No active experiences are available in this organization. You can
						still switch organizations and keep current experience unset.
					</Text>
				)}

				{showExperienceSelector && (
					<Select
						label='Current Experience'
						value={selectedExperienceId}
						onChange={(value) =>
							setSelectedExperienceByOrg((previous) => ({
								...previous,
								[selectedOrgSlug]: value || '',
							}))
						}
						data={selectedExperienceOptions.map((option) => ({
							value: option.value,
							label: option.label,
						}))}
						mb='md'
					/>
				)}

				{selectedExperienceOptions.length === 1 && (
					<TextInput
						label='Current Experience'
						value={selectedExperienceOptions[0]?.label || ''}
						readOnly
						mb='md'
					/>
				)}

				<Group position='right' mt='lg'>
					<Button
						onClick={handleSave}
						loading={isSaving}
						disabled={isLoadingExperiences}
					>
						Save Changes
					</Button>
				</Group>
			</Paper>
		</Box>
	)
}

export default AccountSettingsPage
